import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../config/fbConfig';
import Loader from "./Loader";
import { syncSubjectsWithStudents } from './SyncSubjects';

Modal.setAppElement('#root');

const AddPdfToSubjectModal = ({ isOpen, onClose, classId, subject, onSuccess }) => {
  const [pdfLink, setPdfLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingRemove, setLoadingRemove] = useState(false);

  useEffect(() => {
    if (!isOpen || !subject?.id) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch the class's current subject assignments
        const classRef = doc(db, 'classes', classId);
        const classSnap = await getDoc(classRef);
        const classData = classSnap.exists() ? classSnap.data() : {};
        const currentSubjects = classData.subjects || [];
        const currentSubject = currentSubjects.find(
          sub => sub.id === subject.id
        );
        
        // Set current PDF link if exists
        const assignedLink = currentSubject?.pdfLink || '';
        setPdfLink(assignedLink);
      } catch (error) {
        console.error("Error fetching subject data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isOpen, classId, subject]);

  const handleAssign = async () => {
    setLoading(true);
    try {
      const classRef = doc(db, 'classes', classId);
      const classSnap = await getDoc(classRef);
      if (!classSnap.exists()) return;

      const classData = classSnap.data();
      const currentSubjects = classData.subjects || [];

      // Update the subject with the provided PDF link
      const updatedSubjects = currentSubjects.map(sub => {
        if (sub.id === subject.id) {
          return {
            ...sub,
            pdfLink: pdfLink || ''
          };
        }
        return sub;
      });

      // Ensure the subject exists in the class
      if (!currentSubjects.some(sub => sub.id === subject.id)) {
        updatedSubjects.push({
          id: subject.id,
          teacherId: '',
          pdfLink: pdfLink || ''
        });
      }

      await updateDoc(classRef, { subjects: updatedSubjects });
      await syncSubjectsWithStudents(classId);

      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error assigning PDF link:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async () => {
    setLoadingRemove(true);
    try {
      const classRef = doc(db, 'classes', classId);
      const classSnap = await getDoc(classRef);
      if (!classSnap.exists()) return;

      const classData = classSnap.data();
      const currentSubjects = classData.subjects || [];

      // Remove the PDF link from the subject
      const updatedSubjects = currentSubjects.map(sub => {
        if (sub.id === subject.id) {
          const updatedSubject = { ...sub };
          delete updatedSubject.pdfLink;
          return updatedSubject;
        }
        return sub;
      });

      await updateDoc(classRef, { subjects: updatedSubjects });
      await syncSubjectsWithStudents(classId);

      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error removing PDF link:", error);
    } finally {
      setLoadingRemove(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="custom-modal"
      overlayClassName="custom-modal-overlay"
    >
      <p className="size5 caps">Assign PDF Link to {subject?.name}</p>

      {loading ? (
        <Loader text={"LOADING"} />
      ) : (
        <>
          <input
            type="text"
            placeholder="Paste Google Drive PDF link..."
            value={pdfLink}
            onChange={(e) => setPdfLink(e.target.value)}
            className="input-field1"
            style={{ width: '100%', marginBottom: '16px' }}
          />
          
          {pdfLink && (
            <p className="male-female-stat">
              Current Link: <a href={pdfLink} target="_blank" rel="noopener noreferrer">View PDF</a>
            </p>
          )}

          <div className="row align-center gap-1" style={{ marginTop: '16px' }}>
            <button
              onClick={onClose}
              className="login-button"
              disabled={loading || loadingRemove}
            >
              Cancel
            </button>
            <button
              onClick={handleRemove}
              className="login-button"
              disabled={loading || loadingRemove || !pdfLink}
            >
              {loadingRemove ? 'Removing...' : 'Remove'}
            </button>
            <button
              onClick={handleAssign}
              className="login-button"
              disabled={loading || loadingRemove || !pdfLink}
            >
              {loading ? 'Assigning...' : 'Assign'}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
};

export default AddPdfToSubjectModal;